import { useRef, useState, useEffect } from 'react'
import type { FC, ReactNode } from 'react'
import Croppie, { CroppieOptions } from 'croppie'
import 'croppie/croppie.css'
import { supabase } from '../../../lib/supabase'
import clsx from 'clsx'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Modal } from '../ui/Modal'
import { captureAppException } from '../../../utils/errorCaptureUtils';
type AvatarSize = 'small' | 'medium' | 'large'
type AvatarShape = 'circle' | 'square'

interface AvatarUploadProps {
  currentImageUrl?: string
  onImageUpload: (file: File) => Promise<void>
  size?: AvatarSize
  shape?: AvatarShape
  maxSizeMB?: number
  aspectRatio?: number
  bucketName?: string
  className?: string
  placeholder?: ReactNode
  allowedFileTypes?: string[]
  label?: string
  helperText?: string
  error?: boolean | string
  isRequired?: boolean
  disabled?: boolean
  outputWidth?: number
  outputHeight?: number
  quality?: number
}

const getStoragePathFromUrl = (url: string): string | null => {
  try {
    const storageUrl = new URL(url)
    const match = storageUrl.pathname.match(/\/object\/public\/(.*)/)
    return match ? match[1] : null
  } catch {
    return null
  }
}

const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png']

// Add utility functions for image processing
const resizeImage = (blob: Blob, maxWidth: number, maxHeight: number, quality: number = 0.85): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(blob);
    
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (resizedBlob) => {
          if (resizedBlob) {
            resolve(resizedBlob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/png',
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
  });
};

export const AvatarUpload: FC<AvatarUploadProps> = ({
  currentImageUrl,
  onImageUpload,
  size = 'medium',
  shape = 'circle',
  maxSizeMB = 10,
  aspectRatio = 1,
  bucketName = 'profile-images',
  className = '',
  placeholder,
  allowedFileTypes = DEFAULT_ALLOWED_TYPES,
  label,
  helperText,
  error,
  isRequired = false,
  disabled = false,
  outputWidth = 320,
  outputHeight = 320,
  quality = 0.85
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const previewObjectUrlRef = useRef<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const croppieContainerRef = useRef<HTMLDivElement | null>(null)
  const croppieInstanceRef = useRef<Croppie | null>(null)
  const [recalcSize, setRecalcSize] = useState({ w: 0, h: 0 })

  // Size mappings
  const sizeClasses: Record<AvatarSize, string> = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-32 h-32'
  }

  useEffect(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current)
      previewObjectUrlRef.current = null
    }
    setPreviewUrl(currentImageUrl || null)
  }, [currentImageUrl])

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current)
      }
    }
  }, [])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      if (isRequired && !currentImageUrl) {
        setValidationError('An image is required')
      }
      return
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      setValidationError(`File must be under ${maxSizeMB}MB`)
      return
    }

    if (!allowedFileTypes.includes(file.type)) {
      setValidationError(`Only ${allowedFileTypes.map(type => type.replace('image/', '')).join(', ')} files are allowed`)
      return
    }

    setValidationError(null)
    const reader = new FileReader()
    reader.onload = () => {
      if (reader.result) {
        setSelectedImage(reader.result as string)
        setShowModal(true)
      }
    }
    reader.readAsDataURL(file)
  }

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const initCroppie = (opts?: Partial<CroppieOptions>) => {
    if (!croppieContainerRef.current) return

    if (croppieInstanceRef.current) {
      croppieInstanceRef.current.destroy()
      croppieInstanceRef.current = null
    }

    const containerWidth = croppieContainerRef.current.offsetWidth
    const boundarySize = containerWidth
    const viewportSize = Math.floor(boundarySize * 0.8)

    croppieInstanceRef.current = new Croppie(croppieContainerRef.current, {
      enableExif: true,
      enableOrientation: true,
      enableZoom: true,
      showZoomer: true,
      boundary: { width: boundarySize, height: boundarySize },
      viewport: {
        width: viewportSize,
        height: shape === 'circle' ? viewportSize : viewportSize / aspectRatio,
        type: shape === 'circle' ? 'circle' : 'square'
      },
      ...opts
    })
  }

  useEffect(() => {
    if (!showModal || !croppieContainerRef.current || !selectedImage) return
    initCroppie()
    croppieInstanceRef.current?.bind({ url: selectedImage })
  }, [showModal, selectedImage, recalcSize, shape, aspectRatio])

  useEffect(() => {
    function handleResize() {
      if (showModal) {
        setRecalcSize({ w: window.innerWidth, h: window.innerHeight })
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [showModal])

  const deleteOldImage = async (url: string) => {
    const path = getStoragePathFromUrl(url)
    if (path) {
      try {
        const { error } = await supabase.storage
          .from(bucketName)
          .remove([path])
        if (error) {
          console.error('Error deleting old image:', error)
        }
      } catch (err) {
        captureAppException(err, {
          tags: {
            context: 'avatar_upload',
            action: 'delete_old_image',
            path: path
          }
        })
        console.error('Failed to delete old image:', err)
      }
    }
  }

  const handleSave = async () => {
    if (!croppieInstanceRef.current) return
    setLoading(true)
    try {
      const blob = await croppieInstanceRef.current.result({
        type: 'blob',
        format: 'png',
        size: { width: outputWidth, height: outputHeight },
        // Always export a square crop for storage; keep circular visualization only in UI
        circle: false
      })

      // Image is already at target dimensions from Croppie, but still compress
      const resizedBlob = await resizeImage(blob, outputWidth, outputHeight, quality);

      const timestamp = Date.now()
      const randomString = Math.random().toString(36).substring(2, 15)
      const filename = `${bucketName}-${timestamp}-${randomString}.png`

      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current)
      }

      const file = new File([resizedBlob], filename, { type: 'image/png' })
      const objectUrl = URL.createObjectURL(resizedBlob)
      previewObjectUrlRef.current = objectUrl
      setPreviewUrl(objectUrl)

      if (currentImageUrl && currentImageUrl !== previewUrl) {
        await deleteOldImage(currentImageUrl)
      }

      await onImageUpload(file)
      setShowModal(false)
      setSelectedImage(null)
      setValidationError(null)
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'avatar_upload',
          action: 'save_cropped_image'
        }
      })
      console.error('Image processing error:', err)
      setValidationError('Failed to process image')
    } finally {
      setLoading(false)
    }
  }

  const errorMessage = typeof error === 'string' ? error : validationError

  const defaultPlaceholder = (
    <div className={clsx(
      'flex flex-col items-center justify-center',
      disabled ? 'text-gray-400' : 'text-gray-500'
    )}>
      <PlusIcon className="w-6 h-6" />
      <span className="text-xs mt-1">Upload</span>
    </div>
  )

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {(helperText || errorMessage) && (
        <p className={clsx(
          'text-sm mb-1',
          errorMessage ? 'text-red-500' : 'text-gray-500'
        )}>
          {errorMessage || helperText}
        </p>
      )}

      <div className={clsx(
        'flex flex-col sm:flex-row items-center gap-2 sm:gap-4',
        className
      )}>
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={clsx(
            'relative group cursor-pointer focus:outline-hidden',
            disabled && 'cursor-not-allowed'
          )}
        >
          <div
            className={clsx(
              'relative flex items-center justify-center overflow-hidden',
              'border-2 border-dashed transition-colors duration-200',
              sizeClasses[size],
              shape === 'circle' ? 'rounded-full' : 'rounded-lg',
              previewUrl ? 'border-transparent' : 'border-gray-300',
              !disabled && !previewUrl && 'hover:border-primary-base',
              error || validationError ? 'border-red-300' : 'border-gray-300',
              disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={label || 'avatar'}
                className={clsx(
                  'w-full h-full object-cover',
                  !disabled && 'group-hover:opacity-75 transition-opacity duration-200'
                )}
              />
            ) : placeholder || defaultPlaceholder}
          </div>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept={allowedFileTypes.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {showModal && (
        <Modal 
          isOpen={showModal} 
          onClose={() => {
            setShowModal(false)
            setSelectedImage(null)
            if (previewObjectUrlRef.current) {
              URL.revokeObjectURL(previewObjectUrlRef.current)
              previewObjectUrlRef.current = null
              setPreviewUrl(currentImageUrl || null)
            }
          }}
          size="md"
          mobileFullscreen={false}
          className="mt-0 sm:mt-0 !translate-y-0"
        >
          <div className="p-6">
            <h3 className="text-base font-medium mb-4">Crop Image</h3>
            <div ref={croppieContainerRef} className="w-full min-h-[300px]" />
            <div className="flex justify-end mt-4 gap-2">
              <button
                type="button"
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg',
                  'border border-gray-300 text-gray-700',
                  'hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-base',
                  loading && 'opacity-50 cursor-not-allowed'
                )}
                onClick={() => {
                  setShowModal(false)
                  setSelectedImage(null)
                  if (previewObjectUrlRef.current) {
                    URL.revokeObjectURL(previewObjectUrlRef.current)
                    previewObjectUrlRef.current = null
                    setPreviewUrl(currentImageUrl || null)
                  }
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg',
                  'bg-primary-base text-white',
                  'hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-base',
                  loading && 'opacity-50 cursor-not-allowed'
                )}
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
} 